import { useState, ChangeEvent } from "react";
import {
  Box,
  Grid,
  Tabs,
  Tab,
  Typography,
  useMediaQuery,
  Theme,
  styled,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ShowBase, useRecordContext } from "react-admin";
import ComponentShow, { ShowActions } from "../../../components/PageComponents/ComponentShow";
import InfoStatementTab, { monthLabel } from "./InfoStatementTab";
import InfoLineItemsTab from "./InfoLineItemsTab";
import RecentActivity from "./RecentActivity";
import DetailHeader from "../../../components/PageComponents/DetailHeader";
import { ProjectField } from "../ProjectField";
import { StatementStatusLabel } from "./StatementStatusLabel";
import { formatEuro } from "../../../utils/paymentFormat";

const TabsWrapper = styled(Tabs)(() => ``);

const GasStationStatementShow = () => (
  <ShowBase>
    <Content />
  </ShowBase>
);

const Content = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <ComponentShow
      header={
        <DetailHeader
          kind="Gas station statement"
          title={monthLabel(record.month)}
          status={<StatementStatusLabel />}
          facts={[<ProjectField />, record.totalFeeEur !== undefined && `Total ${formatEuro(record.totalFeeEur)}`]}
        />
      }
    >
      <ContentShow />
    </ComponentShow>
  );
};

const ContentShow = () => {
  const { t }: { t: any } = useTranslation();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));
  const record = useRecordContext();

  const [currentTab, setCurrentTab] = useState<string>("infoStatement");

  const tabs = [{ value: "infoStatement", label: t("Summary") }];
  if ((record?.lineItems || []).length > 0) {
    tabs.push({ value: "infoLineItems", label: t("Line items") });
  }

  const handleTabsChange = (_event: ChangeEvent<{}>, value: string): void => {
    setCurrentTab(value);
  };

  if (isMobile) {
    return (
      <Box sx={{ px: 2, mt: 2, mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <ShowActions resource="gasStationStatements" />
        </Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t("Summary")}
        </Typography>
        <InfoStatementTab />

        {(record?.lineItems || []).length > 0 && (
          <>
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              {t("Line items")}
            </Typography>
            <InfoLineItemsTab />
          </>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3, mb: 4 }}>
      <Grid container direction="row" justifyContent="center" spacing={4} sx={{ px: 4 }}>
        <Grid item xs={9} md={9}>
          <TabsWrapper
            onChange={handleTabsChange}
            value={currentTab}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </TabsWrapper>
        </Grid>
        <Grid item xs={3} md={3} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <ShowActions resource="gasStationStatements" />
        </Grid>
        <Grid item xs={12} md={8}>
          {currentTab === "infoStatement" && <InfoStatementTab />}
          {currentTab === "infoLineItems" && <InfoLineItemsTab />}
        </Grid>
        <Grid item xs={12} md={4}>
          <RecentActivity />
        </Grid>
      </Grid>
    </Box>
  );
};

export default GasStationStatementShow;
